import { Box, Typography } from "@outschool/backpack";
import { useTranslation, withTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  onboardingQuizPath,
  removeParamsFromUrl,
} from "@outschool/routes";
import { withTrackEvent } from "@outschool/ui-analytics";
import { gql, graphql } from "@outschool/ui-apollo";
import {
  AuthTrigger,
  LoginFlowContext,
  persistPostLoginPath,
} from "@outschool/ui-auth";
import {
  Input,
  ModalActionButtons,
  Select,
  Space,
} from "@outschool/ui-legacy-component-library";
import React from "react";
import { withRouter } from "react-router";

import * as Activity from "../../shared/Activity";
import * as Env from "../../shared/Env";
import * as Routes from "../../shared/Routes";
import ClassRequestFragment from "../queries/ClassRequestFragment";
import ErrorMessage from "./ErrorMessage";
import PageModal from "./PageModal";

export const createClassRequestMutation = gql`
  mutation createClassRequest($age: Int!, $content: String!) {
    createClassRequest(age: $age, content: $content) {
      ...ClassRequestFragment
    }
  }
  ${ClassRequestFragment}
`;

function ThanksForFeedback() {
  const { t } = useTranslation("client\\components\\ClassRequestForm");
  return (
    <Box>
      <Typography variant="h3">{t("Thank You")}</Typography>
      <Space y="small" />
      <Typography variant="inherit">
        {t(
          "We appreciate the feedback. We will see how we can bring more of those classes to Outschool.",
        )}
      </Typography>
    </Box>
  );
}

/**
 * Modal form that lets the user submit a class request to Outschool.
 */
@graphql(createClassRequestMutation, { name: "createClassRequest" })
@withRouter
@withTranslation("client\\components\\ClassRequestForm")
@withTrackEvent
export default class ClassRequestForm extends React.Component {
  static contextType = LoginFlowContext;

  constructor(props) {
    super(props);
    this.state = this.defaultState;
    this.close = this.close.bind(this);
    this.open = this.open.bind(this);
    this.submit = this.submit.bind(this);
    this.inputRef = React.createRef(null);
  }

  get defaultState() {
    const location = this.props.location;
    const open = location && location.query.modal === "classRequest";
    return {
      open,
      error: "",
      topic: "",
      age: 0,
      submittedRequest: false,
    };
  }

  close(e) {
    const { history, location } = this.props;
    e && e.preventDefault();
    if (location && location.query.modal) {
      history.replace(
        removeParamsFromUrl(location.pathname + location.search, ["modal"]),
      );
    }
    this.setState({
      open: false,
      error: "",
      topic: "",
      age: 0,
      submittedRequest: false,
    });
  }

  open(e) {
    const { isLoggedIn, location, trackEvent } = this.props;
    e && e.preventDefault();
    trackEvent("Request class clicked");
    if (location && !isLoggedIn) {
      const redirectUrlAfterLoginFlow = addParamsToUrl(
        location.pathname + location.search,
        {
          modal: "classRequest",
        },
      );

      this.context.enterLoginFlow({
        authTrigger: AuthTrigger.REQUEST_CLASS,
        newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
          requestClass: true,
          landingPage: redirectUrlAfterLoginFlow,
        }),
        newUrlAfterLogin: redirectUrlAfterLoginFlow,
        shouldNavigateOverwrite: false,
      });
      return;
    } else {
      this.setState({ open: true });
    }
  }

  submit(e) {
    e.preventDefault();
    const { createClassRequest, t } = this.props;
    let content = this.inputRef.current?.value.trim();
    let age = this.state.age;

    if (content && content.length > 19 && age) {
      createClassRequest({
        variables: {
          age: age,
          content: content,
        },
      })
        .then(() => {
          this.setState({ submittedRequest: true });
        })
        .catch(error => this.setState({ error }));
    } else if (!content && !age) {
      this.setState({ error: t("Please enter a topic and choose an age") });
    } else if (!content) {
      this.setState({ error: t("Please enter a topic") });
    } else if (content.length < 20) {
      this.setState({
        error: t("Please enter more details - 20 characters or more"),
      });
    } else if (!age) {
      this.setState({ error: t("Please choose an age") });
    }
  }

  componentDidMount() {
    this.redirectIfNotLoggedIn(this.props, this.state);
  }

  componentDidUpdate() {
    this.redirectIfNotLoggedIn(this.props, this.state);
  }

  redirectIfNotLoggedIn(props, state) {
    const { open } = state;
    const { history, isLoggedIn, location } = props;
    if (location && open && !isLoggedIn) {
      persistPostLoginPath(location.pathname + location.search);
      history.replace(Routes.signupPath());
    }
  }

  render() {
    const { renderButton, isLoggedIn, t } = this.props;
    const { open, submittedRequest } = this.state;
    return (
      <React.Fragment>
        {renderButton(this.open)}
        {isLoggedIn && (
          <PageModal open={open} onClose={this.close}>
            {submittedRequest && <ThanksForFeedback />}
            {!submittedRequest && (
              <form method="POST" onSubmit={this.submit}>
                <div>
                  <Typography
                    variant="h2"
                    sx={{
                      marginBottom: "0.5em",
                    }}
                  >
                    {t("Request a class")}
                  </Typography>
                  <pre>
                    {t(
                      "Tell us what class topic you’d like to see. We share requested topics and trends with our teachers regularly.",
                    )}
                  </pre>
                  <ErrorMessage
                    value={this.state.error}
                    showStatusPageLink={Env.IS_READ_ONLY_MODE}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      marginTop: "1em",
                    }}
                  >
                    {t("Topic")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "1em",
                    }}
                  >
                    <Box
                      sx={{
                        marginRight: "1em",
                        flexGrow: 1,
                      }}
                    >
                      <Input
                        type="text"
                        placeholder={t("Topic description, be specific")}
                        id="content"
                        ref={this.inputRef}
                        maxLength="255"
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                    </Box>
                    <div>
                      <Select
                        type="number"
                        options={Activity.ageOptions}
                        value={this.state.age}
                        onChange={age => this.setState({ age })}
                        placeholder={t("for age")}
                        includeBlank={false}
                      />
                    </div>
                  </Box>
                </div>
                <ModalActionButtons
                  cancelLabel={t("Cancel")}
                  onCancel={this.close}
                  primaryLabel={t("Send")}
                  primaryType="submit"
                />
              </form>
            )}
          </PageModal>
        )}
      </React.Fragment>
    );
  }
}
